import {defineSlotRecipe} from '@chakra-ui/react3'

const chakraSegmentGroupSlotRecipe = defineSlotRecipe({
    className: 'chakra-segment-group',
    slots: ['root', 'label', 'item', 'itemText', 'itemControl', 'indicator'],
    base: {
        root: {
            '--segment-radius': 'radii.l2',
            borderRadius: 'l2',
            display: 'inline-flex',
            boxShadow: 'inset',
            minW: 'max-content',
            textAlign: 'center',
            position: 'relative',
            isolation: 'isolate',
            bg: 'bg.muted',
            _vertical: {
                flexDirection: 'column'
            }
        },
        item: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
            fontSize: 'sm',
            position: 'relative',
            color: 'fg',
            borderRadius: 'var(--segment-radius)',
            _disabled: {
                opacity: '0.5'
            },
            '&:has(input:focus-visible)': {
                focusRing: 'outside'
            },
            _before: {
                content: '""',
                position: 'absolute',
                bg: 'border',
                transition: 'opacity 0.2s'
            },
            _horizontal: {
                _before: {
                    insetInlineStart: 0,
                    insetBlock: '1.5',
                    width: '1px'
                }
            },
            _vertical: {
                _before: {
                    insetBlockStart: 0,
                    insetInline: '1.5',
                    height: '1px'
                }
            },
            '& + &[data-state=checked], &[data-state=checked] + &, &:first-of-type': {
                _before: {
                    opacity: '0'
                }
            },
            '&[data-state=checked][data-ssr]': {
                shadow: 'sm',
                bg: 'bg',
                borderRadius: 'var(--segment-radius)'
            }
        },
        indicator: {
            shadow: 'sm',
            pos: 'absolute',
            bg: {
                _light: 'bg',
                _dark: 'bg.emphasized'
            },
            width: 'var(--width)',
            height: 'var(--height)',
            top: 'var(--top)',
            left: 'var(--left)',
            zIndex: -1,
            borderRadius: 'var(--segment-radius)'
        }
    },
    variants: {
        size: {
            xs: {
                item: {
                    textStyle: 'xs',
                    px: '3',
                    gap: '1',
                    height: '6'
                }
            },
            sm: {
                item: {
                    textStyle: 'sm',
                    px: '4',
                    gap: '2',
                    height: '8'
                }
            },
            md: {
                item: {
                    textStyle: 'sm',
                    px: '4',
                    gap: '2',
                    height: '10'
                }
            },
            lg: {
                item: {
                    textStyle: 'md',
                    px: '4.5',
                    gap: '3',
                    height: '11'
                }
            }
        }
    },
    defaultVariants: {
        size: 'md'
    }
})

const leapSegmentGroupSlotRecipe = defineSlotRecipe({
    className: 'leap-segment-group',
    slots: ['root', 'label', 'item', 'itemText', 'itemControl', 'indicator'],
    base: {
        root: {
            '--segment-radius': 'radii.l2',
            display: 'inline-flex',
            textAlign: 'center',
            position: 'relative',
            isolation: 'isolate',
            _horizontal: {
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                marginInlineStart: {
                    base: 'calc(-1 * var(--sidebar-inline-padding-start, 0px))',
                    s: 0
                },
                marginInlineEnd: {base: 'calc(-1 * var(--sidebar-inline-padding-end, 0px))', s: 0},
                paddingInlineStart: {base: 'var(--sidebar-inline-padding-start, 0px)', s: 0},
                paddingInlineEnd: {base: 'var(--sidebar-inline-padding-end, 0px)', s: 0},
                scrollPaddingInlineStart: {base: 'var(--sidebar-inline-padding-start, 0px)', s: 0},
                scrollPaddingInlineEnd: {base: 'var(--sidebar-inline-padding-end, 0px)', s: 0}
            },
            _vertical: {
                flexDirection: 'column'
            }
        },
        item: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
            position: 'relative',
            borderRadius: 'var(--segment-radius)',
            _disabled: {
                opacity: '0.5'
            }
        },
        indicator: {
            pos: 'absolute',
            width: 'var(--width)',
            height: 'var(--height)',
            top: 'var(--top)',
            left: 'var(--left)',
            zIndex: -1,
            borderRadius: 'var(--segment-radius)'
        }
    },
    variants: {
        variant: {
            // note: if different variants of buttons are needed rename and add new variant
            buttons: {
                root: {
                    '--segment-radius': 'radii.round',
                    gap: 'spacing-8'
                },
                item: {
                    backgroundColor: 'background.actions.ghost',
                    color: 'text.primary',
                    transition: 'color 0.2s',
                    _hover: {bg: 'background.actions.ghost-hover'},
                    _checked: {
                        color: 'text.primary-inverted'
                    },
                    _focusVisible: {
                        outlineOffset: '2px',
                        outlineStyle: 'solid',
                        outlineWidth: '1px',
                        outlineColor: 'border.strong'
                    }
                },
                indicator: {
                    backgroundColor: 'background.actions.secondary'
                }
            },
            chakra: {
                root: {
                    borderRadius: 'l2',
                    bg: 'bg.muted',
                    boxShadow: 'inset'
                },
                item: {
                    fontSize: 'sm',
                    color: 'fg',
                    _before: {
                        content: '""',
                        position: 'absolute',
                        bg: 'border',
                        transition: 'opacity 0.2s'
                    },
                    _horizontal: {
                        _before: {
                            insetInlineStart: 0,
                            insetBlock: '1.5',
                            width: '1px'
                        }
                    },
                    _vertical: {
                        _before: {
                            insetBlockStart: 0,
                            insetInline: '1.5',
                            height: '1px'
                        }
                    },
                    '& + &[data-state=checked], &[data-state=checked] + &, &:first-of-type': {
                        _before: {
                            opacity: '0'
                        }
                    },
                    '&[data-state=checked][data-ssr]': {
                        shadow: 'sm',
                        bg: 'bg',
                        borderRadius: 'var(--segment-radius)'
                    },
                    '&:has(input:focus-visible)': {
                        focusRing: 'outside'
                    }
                },
                indicator: {
                    shadow: 'sm',
                    bg: {
                        _light: 'bg',
                        _dark: 'bg.emphasized'
                    }
                }
            }
        },
        size: {
            // reuse button sizes for now, but might need to replicate if other variants are used that doesn't resemble buttons
            md: {
                item: {
                    paddingTop: 'spacing-8',
                    paddingBottom: 'spacing-8',
                    paddingLeft: 'spacing-16',
                    paddingRight: 'spacing-16',
                    textStyle: 'heading.small'
                }
            }
        }
    },
    defaultVariants: {
        size: 'md',
        variant: 'chakra'
    }
})

export const segmentGroupSlotRecipe = defineSlotRecipe({
    ...chakraSegmentGroupSlotRecipe,
    ...leapSegmentGroupSlotRecipe
})
